
import React from 'react';

export const NetEarningsCalculation: React.FC = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Calculating Your Net Earnings from the Sale
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Understanding your true net proceeds helps you make informed decisions about timing, pricing, and tax strategies 
            for your apartment building sale.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Net Sale Proceeds Formula</h3>
              
              <div className="bg-teal-50 p-6 rounded-xl mb-6">
                <h4 className="text-lg font-semibold text-teal-800 mb-4">Step-by-Step Calculation:</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center pb-2 border-b border-teal-200">
                    <span className="font-medium text-gray-700">Sale Price</span>
                    <span className="font-bold text-lg">$1,500,000</span>
                  </div>
                  
                  <div className="text-gray-600 font-medium">Less: Transaction Costs</div>
                  <div className="pl-4 space-y-1">
                    <div className="flex justify-between">
                      <span>Real Estate Commission (8%)</span>
                      <span className="text-red-600">-$120,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Title & Escrow (0.7%)</span>
                      <span className="text-red-600">-$10,500</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Transfer Taxes (1%)</span>
                      <span className="text-red-600">-$15,000</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Attorney & Other Fees</span>
                      <span className="text-red-600">-$8,000</span>
                    </div>
                  </div>
                  
                  <div className="flex justify-between pt-2 border-t border-teal-200">
                    <span className="font-medium">Subtotal After Costs</span>
                    <span className="font-medium">$1,346,500</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="font-medium">Less: Outstanding Mortgage</span>
                    <span className="text-red-600">-$800,000</span>
                  </div>
                  
                  <div className="flex justify-between items-center pt-3 border-t-2 border-teal-300 bg-teal-100 px-4 py-2 rounded-lg">
                    <span className="font-bold text-teal-800">Net Sale Proceeds</span>
                    <span className="font-bold text-xl text-teal-800">$546,500</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h5 className="font-semibold text-blue-800 mb-2">Don't Forget About Taxes!</h5>
                <p className="text-blue-700 text-sm">
                  Net proceeds are subject to capital gains tax. Consult with a tax professional to understand your 
                  specific tax liability and explore strategies like 1031 exchanges.
                </p>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/e59cc33b883f74193eab2709024f7b0e.jpg"
              alt="Net sale proceeds calculation and financial analysis"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Factors That Impact Your Net Earnings</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl">
              <div className="w-12 h-12 bg-red-200 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-price-tag-3-line text-2xl text-red-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Sale Price Optimization</h4>
              <p className="text-gray-600 text-sm mb-3">
                Every $10,000 increase in sale price typically nets you $9,200-9,500 after transaction costs.
              </p>
              <div className="text-xs text-red-700 font-medium">
                Impact: High • Control: Medium
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl">
              <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-percent-line text-2xl text-blue-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Commission Negotiation</h4>
              <p className="text-gray-600 text-sm mb-3">
                Reducing commission by 1% saves $15,000 on a $1.5M sale. Commercial rates are often negotiable.
              </p>
              <div className="text-xs text-blue-700 font-medium">
                Impact: High • Control: High
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl">
              <div className="w-12 h-12 bg-green-200 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-line text-2xl text-green-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Timing Strategy</h4>
              <p className="text-gray-600 text-sm mb-3">
                Market timing and tax year considerations can significantly impact your after-tax proceeds.
              </p>
              <div className="text-xs text-green-700 font-medium">
                Impact: Medium • Control: Medium
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-xl">
              <div className="w-12 h-12 bg-yellow-200 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-bank-line text-2xl text-yellow-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Mortgage Payoff</h4>
              <p className="text-gray-600 text-sm mb-3">
                Outstanding loan balance directly reduces proceeds. Consider prepayment penalties in your calculations.
              </p>
              <div className="text-xs text-yellow-700 font-medium">
                Impact: High • Control: Low
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl">
              <div className="w-12 h-12 bg-purple-200 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-receipt-line text-2xl text-purple-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Transaction Costs</h4>
              <p className="text-gray-600 text-sm mb-3">
                Shopping for services and negotiating fees can save thousands in closing costs.
              </p>
              <div className="text-xs text-purple-700 font-medium">
                Impact: Medium • Control: High
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-6 rounded-xl">
              <div className="w-12 h-12 bg-teal-200 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-tax-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Tax Planning</h4>
              <p className="text-gray-600 text-sm mb-3">
                Strategic tax planning, including 1031 exchanges, can preserve more of your proceeds.
              </p>
              <div className="text-xs text-teal-700 font-medium">
                Impact: Very High • Control: Medium
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=financial%20planning%20consultation%20for%20apartment%20building%20sale%2C%20investment%20advisor%20reviewing%20net%20proceeds%20calculation%2C%20professional%20meeting%20with%20charts%20and%20financial%20documents%2C%20wealth%20management%20strategy%20discussion&width=600&height=400&seq=financial-planning-consultation&orientation=landscape"
              alt="Financial planning consultation for apartment sale proceeds"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>

          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Maximizing Your Net Proceeds</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="text-green-600 font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Optimize Property Value</h4>
                    <p className="text-gray-600 text-sm">
                      Focus on improvements that increase NOI rather than cosmetic upgrades. Every $1,000 increase 
                      in annual NOI typically adds $10,000-15,000 to property value.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="text-blue-600 font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Time the Market</h4>
                    <p className="text-gray-600 text-sm">
                      Monitor cap rate trends and interest rates. Selling when cap rates are low (prices high) 
                      maximizes your proceeds.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="text-yellow-600 font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Negotiate All Costs</h4>
                    <p className="text-gray-600 text-sm">
                      Don't accept standard rates. Commission, title, and service fees are often negotiable, 
                      especially on larger transactions.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <span className="text-purple-600 font-bold text-sm">4</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Plan for Taxes</h4>
                    <p className="text-gray-600 text-sm">
                      Consult with tax professionals early to explore strategies like installment sales, 
                      1031 exchanges, or opportunity zones.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-8 rounded-2xl max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Your Net Proceeds Are What Matter Most</h3>
            <p className="text-lg opacity-90 leading-relaxed mb-4">
              While sale price gets the headlines, your net proceeds determine your actual return on investment. 
              Understanding all the costs and strategies involved helps you make decisions that maximize your wealth.
            </p>
            <div className="text-sm opacity-75">
              Remember: Every situation is unique. Consult with qualified professionals for personalized advice.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
